import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Checkbox, CircularProgress, Alert,
  FormControl, FormControlLabel, FormHelperText, FormLabel,
  InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../config';
import CheckItem from '../shared/CheckItem';
import { formatEventDateRange } from '../shared/eventDate';
import usePageTitle from '../shared/hooks/usePageTitle';

const ROLES = ['Teacher', 'Chaperone'];
const TSHIRT_SIZES = ['Unisex S', 'Unisex M', 'Unisex L', 'Unisex XL', 'Unisex 2XL'];
const DIETARY_OPTIONS = ['Vegetarian', 'Vegan', 'Kosher', 'Halal', 'Gluten-free', 'Dairy-free', 'None', 'Other'];

const EMPTY_FORM = {
  schoolId: '', firstName: '', lastName: '', email: '', phone: '',
  role: '', tshirtSize: '', dietary: [], dietaryOther: '',
  mediaPermission: false,
  signatureName: '', signatureDate: new Date().toISOString().split('T')[0],
};

export default function StaffRegistration() {
  usePageTitle('OSSDF Staff & Chaperone Registration');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const schoolFromUrl = searchParams.get('school') || '';

  const [form, setForm] = useState({ ...EMPTY_FORM, schoolId: schoolFromUrl });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [schoolName, setSchoolName] = useState('');
  const [schoolLoadError, setSchoolLoadError] = useState(null);
  const [staffClosed, setStaffClosed] = useState(false);
  const [notRegistered, setNotRegistered] = useState(false); // school hasn't registered for the event
  const [eventDateRange, setEventDateRange] = useState('');

  // Pulled from the Events tab so the festival dates in the media release
  // text don't go stale every year. Best-effort: falls back to no date
  // range shown rather than blocking the form.
  useEffect(() => {
    fetch(`${API_BASE}/api/register/event`)
      .then(res => (res.ok ? res.json() : null))
      .then(event => setEventDateRange(formatEventDateRange(event?.startDate, event?.endDate)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!schoolFromUrl) return;
    fetch(`${API_BASE}/api/schools/${encodeURIComponent(schoolFromUrl)}`)
      .then(res => {
        if (!res.ok) throw new Error('Could not find this school.');
        return res.json();
      })
      .then(data => {
        setSchoolName(data.schoolName);
        setStaffClosed(!!data.staffClosed);
        setNotRegistered(!!data.notRegistered);
      })
      .catch(err => setSchoolLoadError(err.message));
  }, [schoolFromUrl]);

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const check = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.checked }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const toggleDietary = (option) => {
    setForm(prev => {
      const current = Array.isArray(prev.dietary) ? prev.dietary : [];
      if (option === 'None') return { ...prev, dietary: current.includes('None') ? [] : ['None'], dietaryOther: '' };
      const without = current.filter(d => d !== 'None' && d !== option);
      return { ...prev, dietary: current.includes(option) ? without : [...without, option] };
    });
  };

  const validate = () => {
    const e = {};
    if (!form.schoolId.trim()) e.schoolId = 'Required';
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    if (!form.phone.trim()) e.phone = 'Required';
    if (!form.role) e.role = 'Required';
    if (!form.tshirtSize) e.tshirtSize = 'Required';
    if (!form.mediaPermission) e.mediaPermission = 'Required';
    if (!form.signatureName.trim()) e.signatureName = 'Required';
    if (!form.signatureDate) e.signatureDate = 'Required';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); window.scrollTo(0, 0); return; }

    setSubmitting(true);
    setSubmitError(null);

    const dietaryArray = Array.isArray(form.dietary) ? form.dietary : [];
    const dietaryString = dietaryArray.map(d => d === 'Other' && form.dietaryOther ? `Other: ${form.dietaryOther}` : d).join(', ');

    try {
      const res = await fetch(`${API_BASE}/api/register/staff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          schoolId: form.schoolId,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          role: form.role,
          tshirtSize: form.tshirtSize,
          dietary: dietaryString,
          mediaPermission: form.mediaPermission,
          signatureName: form.signatureName,
          signatureDate: form.signatureDate,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Server error ${res.status}`);
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64 }} />
        <Typography variant="h5" fontWeight={700}>Registration submitted!</Typography>
        <Typography color="text.secondary">
          Thank you, {form.firstName}. Your staff/chaperone registration has been received.
        </Typography>
      </Box>
    );
  }

  // ── Registration closed (school not registered for the event, or withdrawn) ──
  if (schoolFromUrl && staffClosed) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={700}>{notRegistered ? 'Registration isn\'t open yet' : 'Registration is closed'}</Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 420 }}>
          {notRegistered
            ? `${schoolName || 'This school'} hasn't registered for this event yet, so teachers and chaperones can't sign up. Register for the event in the teacher portal first.`
            : `${schoolName || 'This school'} has withdrawn from this event and can no longer register teachers or chaperones.`}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6, px: 2 }}>
      <Box sx={{ maxWidth: 640, mx: 'auto' }}>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4, gap: 1 }}>
          <GroupsIcon color="primary" sx={{ fontSize: 48 }} />
          <Typography variant="h4" fontWeight={700}>Staff / Chaperone Registration</Typography>
          <Typography color="text.secondary" align="center">
            Please complete this form only once.  <br />
            If you have changes, please contact your school Dancefest administrator
          </Typography>
        </Box>

        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>School</Typography>
            <TextField
              label="School" required fullWidth size="small"
              value={schoolFromUrl ? schoolName : form.schoolId}
              onChange={schoolFromUrl ? undefined : set('schoolId')}
              error={!!errors.schoolId || !!schoolLoadError}
              helperText={errors.schoolId || schoolLoadError || (schoolFromUrl ? 'Pre-filled from your invite link' : 'Enter the School ID provided by your teacher')}
              InputProps={{ readOnly: !!schoolFromUrl }}
              sx={schoolFromUrl ? { '& .MuiInputBase-root': { bgcolor: 'action.hover' } } : {}}
            />
          </CardContent>
        </Card>

        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>Personal Information</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField label="First name" required size="small" value={form.firstName} onChange={set('firstName')} error={!!errors.firstName} helperText={errors.firstName} />
              <TextField label="Last name" required size="small" value={form.lastName} onChange={set('lastName')} error={!!errors.lastName} helperText={errors.lastName} />
            </Box>
            <TextField label="Email address" required size="small" type="email" fullWidth value={form.email} onChange={set('email')} error={!!errors.email} helperText={errors.email} />
            <TextField label="Phone number" required size="small" type="tel" fullWidth value={form.phone} onChange={set('phone')} error={!!errors.phone} helperText={errors.phone} />
            <FormControl size="small" required error={!!errors.role} fullWidth>
              <InputLabel>Teacher / Chaperone</InputLabel>
              <Select value={form.role} label="Teacher / Chaperone" onChange={set('role')}>
                {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
              {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
            </FormControl>
          </CardContent>
        </Card>

        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>Additional Information</Typography>
            <FormControl size="small" required error={!!errors.tshirtSize} fullWidth>
              <InputLabel>T-shirt size</InputLabel>
              <Select value={form.tshirtSize} label="T-shirt size" onChange={set('tshirtSize')}>
                {TSHIRT_SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
              {errors.tshirtSize && <FormHelperText>{errors.tshirtSize}</FormHelperText>}
            </FormControl>
            <FormControl fullWidth>
              <FormLabel sx={{ fontSize: 14, mb: 0.5, color: 'text.primary', '&.Mui-focused': { color: 'text.primary' } }}>
                Dietary restrictions / allergies
              </FormLabel>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', mt: 0.5 }}>
                {DIETARY_OPTIONS.map(option => (
                  <FormControlLabel key={option} label={option}
                    control={<Checkbox size="small" checked={Array.isArray(form.dietary) && form.dietary.includes(option)} onChange={() => toggleDietary(option)} />}
                  />
                ))}
              </Box>
              {Array.isArray(form.dietary) && form.dietary.includes('Other') && (
                <TextField size="small" fullWidth placeholder="Please specify…"
                  value={form.dietaryOther} onChange={e => setForm(prev => ({ ...prev, dietaryOther: e.target.value }))} sx={{ mt: 1 }} />
              )}
            </FormControl>
          </CardContent>
        </Card>

        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Media Release Information</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Our priority is to keep our participants' identifies safe while offering an event that provides opportunity for all youth who
              choreograph and/or perform original dance compositions to share and celebrate their creative artistry with us and each other.
              <br /><br />
              Please read the following media release form and complete all required fields. If you have questions or are unable to fill out the form,
              please email ossdancefest@gmail.com. Note:  All information in the Media Release Form will be kept confidential with OSSDF and not shared with other parties.
            </Typography>
          </CardContent>
        </Card>

        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Media Release Form</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              I give Ontario Secondary School DANCEFEST (the “Media”) permission to share the video-recorded dance entries and photographs of my school's
              dance entries with the understanding that the video(s) of dance entries, recorded by the events official videographer, and the photographs taken
              by the events official photographer during the festival{eventDateRange ? `, from ${eventDateRange},` : ','} will be shared with teachers following the event
              for teachers, students, and family members to view from their classroom or home.
              <br /><br />
              In addition, DANCEFEST may share the dance entries in a virtual event with a password-protected link on our website, ossdancefest.ca.
              During the live event, entries will not be distributed outside of the event and its participants.
              <br /><br />
              Furthermore, when photographs and other celebrated student work are released on the DANCEFEST website and social media platforms,
              DANCEFEST cannot control or prevent the further distribution or use of the material by those who access the content.
              <br /><br />
              Upon electronic signature of this form, participants agree not to record participants using a mobile or camera device at a live event.
              I understand that DANCEFEST will use the video submission(s) for the purposes of education, festival promotion, and on DANCEFEST's website
              and social media channels.
            </Typography>
            <CheckItem checked={form.mediaPermission} onChange={check('mediaPermission')} error={errors.mediaPermission} label="I provide permission based on the above statement and will follow all requirements." />
          </CardContent>
        </Card>

        <Card elevation={2} sx={{ mb: 4 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>Electronic Signature</Typography>
            <Typography variant="body2" color="text.secondary">
              By typing your name below, you are providing your electronic signature for the Media Release:
            </Typography>
            <TextField label="Name (electronic signature)" required fullWidth size="small"
              value={form.signatureName} onChange={set('signatureName')}
              error={!!errors.signatureName} helperText={errors.signatureName} />

            <Typography variant="body2" color="text.secondary">
              By entering the date below, you CERTIFY the electronic signature for the Media Release:
            </Typography>
            <TextField label="Date" required fullWidth size="small" type="date"
              value={form.signatureDate} onChange={set('signatureDate')}
              InputLabelProps={{ shrink: true }}
              error={!!errors.signatureDate} helperText={errors.signatureDate} />
          </CardContent>
        </Card>

        {submitError && <Alert severity="error" sx={{ mb: 3 }}>{submitError}</Alert>}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/registration')}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit registration'}
          </Button>
        </Box>

      </Box>
    </Box>
  );
}
